/**
 * A Vault's Logs tab: the last lines the Vault keeps in memory, fetched through the Cloud. Newest at the bottom; the
 * view follows new lines while it is at the bottom. Offline, there is nothing to fetch: the empty state names the
 * commands to run on the server instead.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from '../lib/liveparams.ts';
import { useSyncedText } from '../lib/formstate.ts';
import { Api, ApiError, type VaultDetailData } from '../lib/api.ts';
import { ago } from '../lib/format.ts';
import { JOURNAL_COMMAND, STATUS_COMMAND, filterLines, logLevel, splitLevel, type LogLevel } from '../lib/vaultconfig.ts';
import { Kicker, Pill, Surface, cx } from '../design/primitives.tsx';
import { CopyIcon, RestartIcon } from '../components/icons.tsx';
import { Button, Spinner, Switch, inputClass, useToast } from '../components/ui.tsx';

const AUTO_MS = 5000;
const SIZES = [100, 200, 500] as const;
const LEVEL_TEXT: Record<LogLevel, string> = { error: 'text-fail font-semibold', warn: 'text-warn font-semibold', info: 'text-n9', debug: 'text-n7' };

export function ServerCommands({ lead }: { lead: string }) {
  const toast = useToast();
  const cmd = (text: string) => (
    <button key={text} type="button" title="Copy" onClick={() => { void navigator.clipboard?.writeText(text); toast('Copied'); }}
      className="inline-flex items-center gap-2 max-w-full rounded-chip bg-n3 hover:bg-n4 border-0 px-2.5 py-1.5 font-mono text-micro text-n10 cursor-pointer text-left focus-visible:ring-[1.5px] focus-visible:ring-accent group">
      <span className="[overflow-wrap:anywhere]">{text}</span><CopyIcon size={13} className="shrink-0 text-n7 group-hover:text-n10" />
    </button>
  );
  return (
    <div className="flex flex-col gap-2 items-start">
      <p className="m-0 text-small text-n8">{lead}</p>
      {cmd(STATUS_COMMAND)}
      {cmd(JOURNAL_COMMAND)}
    </div>
  );
}

export function LogsTab({ v, tid }: { v: VaultDetailData; tid: string }) {
  const toast = useToast();
  // The filter, the number of lines and auto-refresh are in the URL (replace), so Back returns to the same view
  const [params, setParams] = useSearchParams();
  const sizeParam = Number(params.get('ls'));
  const size: number = (SIZES as readonly number[]).includes(sizeParam) ? sizeParam : 200;
  const auto = params.get('la') === '1';

  // The size and auto-refresh are history steps; typing the filter replaces
  const put = (key: string, value: string | undefined, how: 'push' | 'replace' = 'push') => setParams((p) => { const q = new URLSearchParams(p); if (value) q.set(key, value); else q.delete(key); return q; }, how === 'replace' ? { replace: true } : undefined);
  const setSize = (n: number) => put('ls', n === 200 ? undefined : String(n));
  const setAuto = (on: boolean) => put('la', on ? '1' : undefined);
  const [filter, setFilter] = useSyncedText(params.get('lf') ?? '', (t) => put('lf', t || undefined, 'replace'));
  const [lines, setLines] = useState<string[]>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [at, setAt] = useState<number>();
  const box = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  const load = useCallback(async () => {
    if (!v.online) return;
    setLoading(true);
    try {
      const r = await Api.vaultLogs(tid, v.id, size);
      setLines(r.lines);
      setError(undefined);
      setAt(Date.now());
    } catch (e) {
      setError(e instanceof ApiError ? (e.status === 403 ? 'Only owners can read the logs.' : e.message) : String(e));
    } finally {
      setLoading(false);
    }
  }, [tid, v.id, v.online, size]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!auto || !v.online) return;
    const t = setInterval(() => { if (document.visibilityState === 'visible') void load(); }, AUTO_MS);
    return () => clearInterval(t);
  }, [auto, v.online, load]);

  const shown = filterLines(lines ?? [], filter);
  useLayoutEffect(() => {
    const el = box.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [shown.length, lines]);
  const onScroll = () => {
    const el = box.current;
    if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  if (!v.online) {
    return (
      <Surface className="p-8 flex flex-col gap-4 items-start">
        <span className="text-body font-medium">The Vault is offline, so its log cannot be fetched</span>
        <ServerCommands lead="Look at it on the server:" />
      </Surface>
    );
  }

  return (
    <Surface className="p-5 flex flex-col gap-4 min-w-0">
      <div className="flex items-center gap-x-4 gap-y-3 flex-wrap">
        <Kicker className="mr-auto">Log</Kicker>
        <div className="inline-flex bg-n3 rounded-full p-0.5" role="group" aria-label="Number of lines">
          {SIZES.map((n) => <Pill key={n} active={size === n} onClick={() => setSize(n)}>{n}</Pill>)}
        </div>
        <label className="inline-flex items-center gap-2 text-small text-n9">
          <Switch label="Refresh every 5 seconds" checked={auto} onChange={setAuto} />Auto-refresh
        </label>
        <Button variant="secondary" disabled={loading} onClick={() => void load()}><RestartIcon size={14} />Refresh</Button>
        <Button variant="secondary" disabled={!shown.length} onClick={() => { void navigator.clipboard?.writeText(shown.join('\n')); toast('Copied'); }}><CopyIcon size={14} />Copy all</Button>
      </div>
      <input aria-label="Filter lines" placeholder="Filter lines" value={filter} onChange={(e) => setFilter(e.target.value)} className={cx(inputClass, 'max-w-sm')} />
      {error && <p className="m-0 text-small text-fail" role="alert">{error}</p>}
      {!lines && !error && <Spinner />}
      {lines && (
        <div ref={box} onScroll={onScroll} tabIndex={0} role="log" aria-label="Vault log"
          className="rounded-chip bg-n3 p-3 h-[min(560px,60vh)] overflow-auto font-mono text-micro leading-5">
          {shown.length === 0 ? (
            <span className="text-n7 font-sans text-small">{lines.length === 0 ? 'The Vault has no log lines yet.' : 'No line matches the filter.'}</span>
          ) : shown.map((l, i) => {
            const [a, w, b] = splitLevel(l);
            const level = logLevel(l);
            return (
              <div key={i} className="whitespace-pre-wrap [overflow-wrap:anywhere] text-n9">
                {a}{w && <span className={level ? LEVEL_TEXT[level] : undefined}>{w}</span>}{b}
              </div>
            );
          })}
        </div>
      )}
      {lines && <p className="m-0 text-micro text-n7">{shown.length === lines.length ? `${lines.length} lines` : `${shown.length} of ${lines.length} lines`}{at ? ` · fetched ${ago(at)}` : ''}</p>}
    </Surface>
  );
}
